import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, DollarSign, Package, Wrench, Users as UsersIcon, Search } from 'lucide-react';
import { SortableHeader } from '@/components/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Invoices() {
  const { token, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('invoiceDate');
  const [sortDir, setSortDir] = useState('desc');
  
  const [formData, setFormData] = useState({
    jobId: '',
    poNumber: '',
    type: 'MATERIAL',
    vendorName: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    amount: '',
    markupPctApplied: '',
    attachmentUrl: ''
  });

  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];

      if (isOffice) {
        promises.push(
          axios.get(`${API}/settings`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
      }

      const results = await Promise.all(promises);
      setInvoices(results[0].data);
      setJobs(results[1].data);
      if (results[2]) {
        setSettings(results[2].data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getJobInfo = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? { jobNumber: job.jobNumber, poNumber: job.poNumber, markupOverride: job.markupDefaultOverride } : { jobNumber: jobId, poNumber: '', markupOverride: null };
  };

  const getDefaultMarkup = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job && job.markupDefaultOverride !== null && job.markupDefaultOverride !== undefined) {
      return job.markupDefaultOverride;
    }
    return settings?.defaultMarkupPct || 15.0;
  };

  const calculateTotalCharge = (amount, markupPct) => {
    const amountNum = parseFloat(amount) || 0;
    const markupNum = parseFloat(markupPct) || 0;
    return amountNum + (amountNum * markupNum / 100);
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        jobId: invoice.jobId,
        poNumber: invoice.poNumber,
        type: invoice.type,
        vendorName: invoice.vendorName,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        amount: invoice.amount.toString(),
        markupPctApplied: invoice.markupPctApplied.toString(),
        attachmentUrl: invoice.attachmentUrl || ''
      });
    } else {
      setEditingInvoice(null);
      setFormData({
        jobId: '',
        poNumber: '',
        type: 'MATERIAL',
        vendorName: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        amount: '',
        markupPctApplied: '',
        attachmentUrl: ''
      });
    }
    setDialogOpen(true);
  };

  const handleJobChange = (jobId) => {
    const jobInfo = getJobInfo(jobId);
    const defaultMarkup = getDefaultMarkup(jobId);
    setFormData({
      ...formData,
      jobId,
      poNumber: jobInfo.poNumber,
      markupPctApplied: defaultMarkup.toString()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        markupPctApplied: formData.markupPctApplied ? parseFloat(formData.markupPctApplied) : null
      };

      if (editingInvoice) {
        await axios.patch(`${API}/invoices/${editingInvoice.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice updated successfully');
      } else {
        await axios.post(`${API}/invoices`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingInvoice) return;

    try {
      await axios.delete(`${API}/invoices/${deletingInvoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingInvoice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'MATERIAL':
        return <Package className='w-5 h-5' />;
      case 'EQUIPMENT':
        return <Wrench className='w-5 h-5' />;
      case 'SUBCONTRACTOR':
        return <UsersIcon className='w-5 h-5' />;
      default:
        return <FileText className='w-5 h-5' />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'MATERIAL':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'EQUIPMENT':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'SUBCONTRACTOR':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const totalCharges = invoices.reduce((sum, inv) => sum + inv.totalCharge, 0);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...invoices];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(inv =>
        inv.vendorName?.toLowerCase().includes(q) ||
        inv.invoiceNumber?.toLowerCase().includes(q) ||
        getJobInfo(inv.jobId).jobNumber?.toLowerCase().includes(q) ||
        inv.type?.toLowerCase().includes(q)
      );
    }
    if (sortColumn) {
      result.sort((a, b) => {
        let aVal, bVal;
        switch (sortColumn) {
          case 'jobNumber': aVal = getJobInfo(a.jobId).jobNumber; bVal = getJobInfo(b.jobId).jobNumber; break;
          case 'vendorName': aVal = a.vendorName || ''; bVal = b.vendorName || ''; break;
          case 'invoiceNumber': aVal = a.invoiceNumber || ''; bVal = b.invoiceNumber || ''; break;
          case 'invoiceDate': aVal = a.invoiceDate || ''; bVal = b.invoiceDate || ''; break;
          case 'type': aVal = a.type || ''; bVal = b.type || ''; break;
          case 'amount': aVal = a.amount || 0; bVal = b.amount || 0; break;
          case 'totalCharge': aVal = a.totalCharge || 0; bVal = b.totalCharge || 0; break;
          default: return 0;
        }
        if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
      });
    }
    return result;
  }, [invoices, searchQuery, sortColumn, sortDir, jobs]);

  if (loading) {
    return (
      <div className='p-4 md:p-8'>
        <div className='text-center text-slate-600'>Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className='p-4 md:p-8' data-testid='invoices-page'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900'>Invoices</h1>
          <p className='mt-2 text-lg text-slate-700'>Manage vendor invoices and costs</p>
        </div>
        {isOffice && (
          <Button
            onClick={() => handleOpenDialog()}
            data-testid='create-invoice-button'
            className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95'
          >
            <Plus className='w-5 h-5 mr-2' />
            New Invoice
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Invoices</p>
            <p className='text-3xl font-bold text-[#1A3668] mt-2'>{invoices.length}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Invoice Amount</p>
            <p className='text-3xl font-bold text-slate-700 mt-2'>{formatCurrency(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total with Markup</p>
            <p className='text-3xl font-bold text-[#16A34A] mt-2'>{formatCurrency(totalCharges)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {invoices.length > 0 && (
        <div className='mb-4'>
          <div className='relative max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input data-testid='invoice-search' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search invoices...' className='pl-9 rounded-sm border-2' />
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-12 text-center'>
            <FileText className='w-12 h-12 mx-auto text-slate-400 mb-4' />
            <p className='text-slate-600 mb-4'>No invoices yet</p>
            {isOffice && (
              <Button
                onClick={() => handleOpenDialog()}
                variant='outline'
                className='border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm'
              >
                Create your first invoice
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse' data-testid='invoices-table'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <SortableHeader column='jobNumber' label='Job' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='type' label='Type' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='vendorName' label='Vendor' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='invoiceNumber' label='Invoice #' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='invoiceDate' label='Date' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='amount' label='Amount' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='text-right' />
                <th className='text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Markup</th>
                <SortableHeader column='totalCharge' label='Total' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='text-right' />
                {isOffice && <th className='text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700'>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((invoice) => (
                <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`} className='border-b border-slate-200 hover:bg-slate-50'>
                  <td className='p-4 text-sm font-medium text-slate-700'>{getJobInfo(invoice.jobId).jobNumber}</td>
                  <td className='p-4'>
                    <Badge className={`rounded-sm flex items-center gap-1 w-fit ${getTypeColor(invoice.type)}`}>
                      {getTypeIcon(invoice.type)}
                      <span className='text-xs'>{invoice.type}</span>
                    </Badge>
                  </td>
                  <td className='p-4 text-sm font-medium text-slate-700'>{invoice.vendorName}</td>
                  <td className='p-4 text-sm font-mono text-slate-600'>{invoice.invoiceNumber}</td>
                  <td className='p-4 text-sm text-slate-600'>{invoice.invoiceDate}</td>
                  <td className='p-4 text-right text-sm font-mono text-slate-700'>{formatCurrency(invoice.amount)}</td>
                  <td className='p-4 text-right text-sm font-mono text-slate-600'>{invoice.markupPctApplied.toFixed(1)}%</td>
                  <td className='p-4 text-right text-sm font-mono font-bold text-[#16A34A]'>{formatCurrency(invoice.totalCharge)}</td>
                  {isOffice && (
                    <td className='p-4 text-center'>
                      <div className='flex gap-2 justify-center'>
                        <Button
                          onClick={() => handleOpenDialog(invoice)}
                          data-testid={`edit-invoice-${invoice.id}`}
                          variant='outline'
                          size='sm'
                          className='border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                        >
                          <Pencil className='w-3 h-3' />
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingInvoice(invoice);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-invoice-${invoice.id}`}
                          variant='outline'
                          size='sm'
                          className='border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm'
                        >
                          <Trash2 className='w-3 h-3' />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {isOffice && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className='sm:max-w-[700px] rounded-sm max-h-[90vh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle className='text-2xl font-bold'>
                {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
              </DialogTitle>
              <DialogDescription>
                {editingInvoice ? 'Update vendor invoice details' : 'Enter vendor invoice for a job'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className='space-y-4 py-4'>
                <div>
                  <Label htmlFor='jobId' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                    Job *
                  </Label>
                  <Select
                    value={formData.jobId}
                    onValueChange={handleJobChange}
                  >
                    <SelectTrigger data-testid='invoice-job-select' className='mt-2 rounded-sm border-2'>
                      <SelectValue placeholder='Select job' />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.jobNumber} - {job.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='poNumber' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      PO Number *
                    </Label>
                    <Input
                      id='poNumber'
                      data-testid='invoice-po-input'
                      value={formData.poNumber}
                      onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                      placeholder='PO-2024-001'
                      required
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                  <div>
                    <Label htmlFor='type' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Type *
                    </Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger data-testid='invoice-type-select' className='mt-2 rounded-sm border-2'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='MATERIAL'>Material</SelectItem>
                        <SelectItem value='EQUIPMENT'>Equipment</SelectItem>
                        <SelectItem value='SUBCONTRACTOR'>Subcontractor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='vendorName' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Vendor Name *
                    </Label>
                    <Input
                      id='vendorName'
                      data-testid='vendor-name-input'
                      value={formData.vendorName}
                      onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                      placeholder='ABC Supply Co'
                      required
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                  <div>
                    <Label htmlFor='invoiceNumber' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Invoice Number *
                    </Label>
                    <Input
                      id='invoiceNumber'
                      data-testid='invoice-number-input'
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                      placeholder='INV-12345'
                      required
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-3 gap-4'>
                  <div>
                    <Label htmlFor='invoiceDate' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Invoice Date *
                    </Label>
                    <Input
                      id='invoiceDate'
                      data-testid='invoice-date-input'
                      type='date'
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                      required
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                  <div>
                    <Label htmlFor='amount' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Amount *
                    </Label>
                    <Input
                      id='amount'
                      data-testid='invoice-amount-input'
                      type='number'
                      step='0.01'
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder='5000.00'
                      required
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                  <div>
                    <Label htmlFor='markupPctApplied' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                      Markup %
                    </Label>
                    <Input
                      id='markupPctApplied'
                      data-testid='markup-pct-input'
                      type='number'
                      step='0.1'
                      value={formData.markupPctApplied}
                      onChange={(e) => setFormData({ ...formData, markupPctApplied: e.target.value })}
                      placeholder='15.0'
                      className='mt-2 rounded-sm border-2'
                    />
                  </div>
                </div>

                {formData.amount && formData.markupPctApplied && (
                  <div className='p-4 bg-green-50 border-2 border-green-200 rounded-sm'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-bold uppercase tracking-wider text-slate-700'>Total Charge:</span>
                      <span className='text-2xl font-bold text-[#16A34A]'>
                        {formatCurrency(calculateTotalCharge(formData.amount, formData.markupPctApplied))}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor='attachmentUrl' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                    Attachment URL (Optional)
                  </Label>
                  <Input
                    id='attachmentUrl'
                    data-testid='attachment-url-input'
                    value={formData.attachmentUrl}
                    onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
                    placeholder='https://...'
                    className='mt-2 rounded-sm border-2'
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setDialogOpen(false)}
                  className='border-2 border-slate-200 rounded-sm'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  data-testid='submit-invoice-button'
                  disabled={submitting}
                  className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
                >
                  {submitting ? 'Saving...' : editingInvoice ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {isOffice && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className='rounded-sm'>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete invoice <strong>{deletingInvoice?.invoiceNumber}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                data-testid='confirm-delete-invoice'
                className='bg-red-600 hover:bg-red-700 rounded-sm'
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
